import type { WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { useListsStore } from '@shared/model'

import { useDisciplineBalancesStore } from './balances/store'
import { useDisciplinePrivileges } from './discipline-privileges.hook'
import { useDisciplineFiltersDraft } from './discipline-filters-draft.hook'
import { disciplineBalancesFiltersToQuery } from '../lib/discipline-balances-query'

interface FiltersDisciplineBalancesReturn {
  refReasons: WritableComputedRef<string[]>
  refResponsible: WritableComputedRef<string | undefined>
  reasonOptions: ComputedRef<AppSelectOption[]>
  responsibleOptions: ComputedRef<AppSelectOption[]>
  isLoading: ComputedRef<boolean>
  canManageBalances: ComputedRef<boolean>
  search: () => void
  clearFilters: () => void
  openEditBalance: () => void
}

const toOptions = (record: Record<string, string> | undefined): AppSelectOption[] =>
  Object.entries(record ?? {}).map(([value, text]) => ({ value, text }))

export const useFiltersDisciplineBalances = (): FiltersDisciplineBalancesReturn => {
  const store = useDisciplineBalancesStore()
  const listsStore = useListsStore()
  const { canManageBalances } = useDisciplinePrivileges()

  const { refDraft, isLoading, patchDraft, search, clearFilters } = useDisciplineFiltersDraft({
    applied: () => store.getFilters,
    onApply: filters => store.setFilters(filters),
    onReset: () => store.resetFilters(),
    toQuery: disciplineBalancesFiltersToQuery,
    loading: () => store.isLoading,
  })

  const refReasons = computed<string[]>({
    get: () => refDraft.value.reasons,
    set: (value) => {
      patchDraft({ reasons: value })
    },
  })

  const refResponsible = computed<string | undefined>({
    get: () => refDraft.value.responsible ?? undefined,
    set: (value) => {
      patchDraft({ responsible: value ?? null })
    },
  })

  const reasonOptions = computed<AppSelectOption[]>(() => toOptions(listsStore.getLists?.balance.reason))
  const responsibleOptions = computed<AppSelectOption[]>(() => toOptions(listsStore.getLists?.balance.responsible))

  const openEditBalance = (): void => {
    store.setModalOpen(true)
  }

  return {
    refReasons,
    refResponsible,
    reasonOptions,
    responsibleOptions,
    isLoading,
    canManageBalances,
    search,
    clearFilters,
    openEditBalance,
  }
}
